/**
 * V&V Generation Card — centered hero (icon → title → description →
 * button) matching Step 0's `.approvedHero` pattern. Renders only
 * during pre-generation states (idle / generating / failed); once the
 * table has rows VVSection unmounts this entirely.
 *
 * VVSection's auto-fetch on mount handles the "already-completed"
 * case, so this card never needs its own existing-status probe.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './VVGenerationCard.module.css';
import {
  getVVRecommendationsStatus,
  getVVRecommendations,
  generateVVRecommendations,
} from '../../../../api/vvRecommendations';

const ShieldCheckIcon = ({ size = 72 }) => (
  <svg className={styles.heroIcon} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const BoltIcon = () => (
  <svg className={styles.buttonIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const RetryIcon = () => (
  <svg className={styles.buttonIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const VVGenerationCard = ({
  threadId,
  onGenerationComplete,
  disabled = false,
  analysisMode = 'individual',
}) => {
  const [status, setStatus] = useState('idle'); // idle, generating, failed
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResponse = await getVVRecommendationsStatus(threadId);

        if (statusResponse?.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setProgress(null);
          const vvData = await getVVRecommendations(threadId);
          if (onGenerationComplete) onGenerationComplete(vvData);
        } else if (statusResponse?.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('failed');
          setError(statusResponse.error || 'Generation failed');
          setProgress(null);
        } else if (statusResponse?.status === 'generating') {
          setProgress({
            total: statusResponse.total_requirements,
            generated: statusResponse.recommendations_generated,
          });
        }
      } catch (err) {
        console.error('Error polling V&V status:', err);
      }
    }, 2000);
  }, [threadId, onGenerationComplete]);

  const handleGenerate = async () => {
    if (disabled || status === 'generating') return;
    setStatus('generating');
    setError(null);
    setProgress(null);
    try {
      await generateVVRecommendations(threadId, analysisMode);
      startPolling();
    } catch (err) {
      console.error('Failed to start V&V generation:', err);
      setStatus('failed');
      setError(err.message || 'Failed to start generation');
    }
  };

  return (
    <div className={styles.hero}>
      <div className={styles.heroIconWrap}>
        <ShieldCheckIcon size={72} />
      </div>
      <h2 className={styles.heroTitle}>V&amp;V Recommendations</h2>
      <p className={styles.heroSubtitle}>ISO 21434 Section 10.4</p>
      <p className={styles.heroDesc}>
        Generate verification &amp; validation recommendations for each cybersecurity
        requirement based on CAL levels.
      </p>

      {status === 'generating' && (
        <div className={styles.progressRow}>
          <span className={styles.spinner} />
          <span className={styles.progressText}>
            Generating V&amp;V recommendations…
            {progress && progress.total > 0 && ` (${progress.generated || 0}/${progress.total})`}
          </span>
        </div>
      )}

      {error && (
        <p className={styles.errorText} role="alert">{error}</p>
      )}

      <div className={styles.heroActions}>
        {status === 'idle' && (
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleGenerate}
            disabled={disabled}
          >
            <BoltIcon />
            Generate V&amp;V Recommendations
          </button>
        )}
        {status === 'failed' && (
          <button type="button" className={styles.retryButton} onClick={handleGenerate}>
            <RetryIcon />
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

export default VVGenerationCard;
